import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import { Button, ButtonType } from '../Button';
import { Modal } from '../Modal';

// Angular source: apps/angular/src/js/ng/parent/bookings/modals/booking-success-modal.*
// Shown after a booking request is submitted successfully.
interface Props {
  isOpen: boolean;
  onClose: () => void;
  recipientName?: string;
  variant?: 'default' | 'agency' | 'promo';
  promoText?: string;
}

export const BookingSuccessModal = ({
  isOpen,
  onClose,
  recipientName,
  variant = 'default',
  promoText,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Modal isOpen={isOpen} onClose={onClose} className='w-full max-w-md'>
      <Button
        aria-label={t('common.close')}
        onClick={onClose}
        className='absolute right-2 top-2 text-gray-400 hover:text-gray-500 transition-colors'
      >
        <FontAwesomeIcon icon={faXmark} className='w-5 h-5' />
      </Button>
      <div className='text-5xl text-center mb-4' aria-hidden='true'>
        🎉
      </div>
      {variant === 'agency' ? (
        <>
          <h3 className='font-semibold mb-2'>{t('components.modals.bookingSuccess.agencyTitle')}</h3>
          <p className='mb-4'>{t('components.modals.bookingSuccess.agencyBody')}</p>
        </>
      ) : variant === 'promo' ? (
        <>
          <h3 className='font-semibold mb-2'>{t('components.modals.bookingSuccess.promoTitle')}</h3>
          <p className='mb-4'>
            {promoText ??
              t('components.modals.bookingSuccess.promoBodyDefault', {
                recipientName:
                  recipientName ??
                  t('components.modals.bookingSuccess.promoBodyDefaultRecipient'),
              })}
          </p>
        </>
      ) : (
        <>
          <h3 className='font-semibold mb-2'>{t('components.modals.bookingSuccess.defaultTitle')}</h3>
          <p className='mb-4'>
            <Trans
              i18nKey='components.modals.bookingSuccess.defaultBody'
              values={{
                recipientName:
                  recipientName ||
                  t('components.modals.bookingSuccess.defaultRecipient'),
              }}
              components={{ recipient: <b /> }}
            />
          </p>
        </>
      )}
      <div className='text-center'>
        <Button buttonType={ButtonType.primary} onClick={onClose}>
          {t('components.modals.bookingSuccess.ok')}
        </Button>
      </div>
    </Modal>
  );
};
